import { useEffect, createRef, useState } from 'react';
import { FormErrorMessageProps, InputProps, Flex, Box } from '@chakra-ui/react';
import { FaTimes } from 'react-icons/fa';
import { FormErrorMessage } from 'components/UI/forms';
import { Input } from 'components/UI/Input';
import { mapMimeTypesToText } from 'utils/helpers';
import { Button } from 'components/UI/Button';
import { bucketUrl } from 'utils/files';
import { Text } from 'components/UI/Text';
import { SimpleGrid } from 'components/UI/SimpleGrid';
import { Image } from 'components/UI/Image';
import { IconButton } from 'components/UI/IconButton';

type UploadedFile = {
  name: string;
  url: string;
};

export type FileUploadProps = Omit<InputProps, 'value'> & {
  error?: string;
  errorProps?: FormErrorMessageProps;
  onChange: (files?: File[] | string) => void;
  value?: UploadedFile[] | string | File[];
};

const checkIfUploadedFile = (file: File | UploadedFile): file is UploadedFile => !(file as File).type;

const FileUpload = ({
  name,
  errorProps = {},
  size = 'md',
  onChange,
  value,
  error,
  accept,
  isDisabled,
  ...props
}: FileUploadProps) => {
  const fileUploader = createRef<HTMLInputElement>();
  const [files, setFiles] = useState<Array<File | UploadedFile> | string>();

  useEffect(() => {
    setFiles(typeof value === 'string' ? [{ name: value, url: `${bucketUrl}/${value}` }] : value);
  }, [value]);

  const handleChangeFile = (event: React.FormEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const newFiles = Array.from((event.target as HTMLInputElement).files ?? []);

    onChange(newFiles);
  };

  const handleClearFiles = () => onChange(undefined);

  const handleFileSelectClick = () => {
    if (!fileUploader?.current) return;
    fileUploader.current.click();
  };

  const renderPreviews =
    files && Array.isArray(files)
      ? files
          .filter((file: File | UploadedFile) => checkIfUploadedFile(file) || (file as File).type.includes('image'))
          .map((file: File | UploadedFile, i: number) => (
            <SimpleGrid columns={files.length} key={file.name}>
              <Image
                maxW={300}
                maxH={100}
                mx={3}
                src={file instanceof File ? URL.createObjectURL(file) : file.url}
                alt={`Preview ${i}`}
              />
            </SimpleGrid>
          ))
      : null;

  const renderClear =
    files && files.length > 0 ? (
      <IconButton aria-label="Clear Files" isDisabled={isDisabled} icon={<FaTimes />} onClick={handleClearFiles} />
    ) : null;

  const renderFileText =
    files && Array.isArray(files) && files.length > 0
      ? files
          .filter((file) => file instanceof File && file.type)
          .map((file) => file.name)
          .join(', ')
      : 'No files selected.';

  const renderMimeTypes =
    accept && accept !== '*' ? (
      <Box mt={4}>
        <Text as="small" mt={2} fontSize="sm">
          {mapMimeTypesToText(accept)}
        </Text>
      </Box>
    ) : null;
  return (
    <Box>
      <Flex w="full" alignItems="center" gap={4}>
        <Button
          type="button"
          isDisabled={isDisabled}
          onClick={() => {
            handleFileSelectClick();
          }}
        >
          Upload
        </Button>
        <Input
          colorScheme="primaryThemeColour"
          {...props}
          size={size}
          onChange={handleChangeFile}
          ref={fileUploader}
          name={name}
          id={name}
          type="file"
          containerProps={{ display: 'none' }}
        />
        <Text
          as="span"
          sx={{
            color: 'base2FontColour.500',
            maxW: 300,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontSize: 'sm',
            whiteSpace: 'nowrap',
          }}
        >
          {renderFileText}
        </Text>
        {renderPreviews}
        {renderClear}
      </Flex>

      {renderMimeTypes}

      <FormErrorMessage {...errorProps}>{error}</FormErrorMessage>
    </Box>
  );
};

export { FileUpload };
